/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import api, { isLoggedIn, logOut } from '@/utils/api';

interface UserProfile {
  status: boolean;
  data: {
    id: number;
    project_id: number;
    public_id: string;
    telegram_id: string | null;
    is_registered: boolean;
    is_email_verified: boolean;
    is_admin: boolean;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    dob: string | null;
    email: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    address: string | null;
  };
  ref_code: {
    id: number;
    user_id: number;
    code: string;
    name: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  referrer: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

export interface BalanceCoin {
  id: number;
  iso: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface BalanceRate {
  symbol: string;
  rateFloat: number;
  updateTime: number;
  serviceName: string;
}

export interface Balance {
  id: number;
  user_id: number;
  type: string;
  coin_id: number;
  balance_available: string;
  balance_frozen: string;
  createdAt: string;
  updatedAt: string;
  coin: BalanceCoin;
  rate: BalanceRate;
  balance_available_usdt: string;
  balance_frozen_usdt: string;
}

export type BalancesResponse = Balance[];

export interface ReferralUserAddress {
  id?: number;
  user_id?: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface ReferralUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: ReferralUserAddress;
}

export interface Referral {
  id?: number;
  user_id?: number;
  referrer_id?: number;
  parent_id?: number;
  level?: number;
  link_id?: number;
  name?: string;
  email?: string;
  isRoot?: boolean;
  referrerId?: number;
  user?: ReferralUser;
  link?: {
    code?: string;
    createdAt?: string;
  };
  status?: string;
  earnings?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface ReferralTreeResponse {
  currentPageResult?: Referral[];
  totalCount?: number;
  totalPages?: number;
}

export interface ReferralLevelStat {
  level: number;
  count: number;
}

export interface ReferralLevelStatsResponse {
  currentPageResult?: ReferralLevelStat[];
  totalPages?: number;
  totalCount?: ReferralLevelStat[]; // Complete list of all levels with counts
}

export const account = {
  state: {
    isLoggedIn: Boolean(localStorage.getItem('tk')),
    isLoading: false,
    user: null,
    error: null,
    profileLoaded: false,
    profileLoading: false,
    passwordReseting: { status: false, userData: null },
    currentPlaidLink: null,
    changesList: [],
    stripeSubscriptionStatus: false,
    cryptoWallets: {},
    addresses: {},
    balances: {},
    currencies: {},
  },
  getters: {
    // Authentication getters
    isLoggedIn(state: any) {
      return state.isLoggedIn;
    },
    isAuthenticated(state: any) {
      return state.isLoggedIn;
    },
    isUserLoading(state: any) {
      return state.isLoading;
    },
    isProfileLoading(state: any) {
      return state.profileLoading;
    },
    isProfileLoaded(state: any) {
      return state.profileLoaded;
    },
    authError(state: any) {
      return state.error;
    },
    
    // User profile getters
    currentUser(state: any) {
      return state.user;
    },
    userEmail(state: any) {
      return state.user?.data?.email || null;
    },
    userName(state: any) {
      if (!state.user?.data) return null;
      const { first_name, last_name } = state.user.data;
      if (first_name && last_name) return `${first_name} ${last_name}`;
      if (first_name) return first_name;
      if (last_name) return last_name;
      return state.user.data.email;
    },
    userFullName(state: any) {
      if (!state.user?.data) return null;
      const { first_name, last_name } = state.user.data;
      return first_name && last_name ? `${first_name} ${last_name}` : null;
    },
    isEmailVerified(state: any) {
      return state.user?.data?.is_email_verified || false;
    },
    isAdmin(state: any) {
      return state.user?.data?.is_admin || false;
    },
    userReferralCode(state: any) {
      return state.user?.ref_code?.code || null;
    },
    userReferrer(state: any) {
      return state.user?.referrer || null;
    },
    
    // Existing getters
    getPasswordReseting: (state: any) => state.passwordReseting,
    getCurrentPlaidLink: (state: any) => state.currentPlaidLink,
    getCurrentStripeLink: (state: any) => state.currentStripeLink,
    getChangesList: (state: any) => state.changesList,
    getStripeSubscriptionIsActive: (state: any) => state.stripeSubscriptionStatus,
    getWalletAddress: (state: any) => (network: 'BSC') => state.cryptoWallets[network] ?? null,
    getAddress: (state: any) => (currency: string) => state.addresses[currency] ?? null,
    getBalances: (state: any) => state.balances,
    getCurrencies: (state: any) => state.currencies,
  },
  mutations: {
    // Authentication mutations
    UPDATE_IS_LOGGED_IN(state: any, val: boolean) {
      state.isLoggedIn = val;
    },
    SET_LOADING(state: any, loading: boolean) {
      state.isLoading = loading;
    },
    SET_PROFILE_LOADING(state: any, loading: boolean) {
      state.profileLoading = loading;
    },
    SET_PROFILE_LOADED(state: any, loaded: boolean) {
      state.profileLoaded = loaded;
    },
    SET_AUTHENTICATED(state: any, isLoggedIn: boolean) {
      state.isLoggedIn = isLoggedIn;
    },
    SET_USER(state: any, user: UserProfile | null) {
      state.user = user;
      state.profileLoaded = !!user;
    },
    SET_ERROR(state: any, error: string | null) {
      state.error = error;
    },
    CLEAR_ERROR(state: any) {
      state.error = null;
    },
    CLEAR_USER(state: any) {
      state.user = null;
      state.isLoggedIn = false;
      state.profileLoaded = false;
      state.profileLoading = false;
      // Clear addresses when user logs out
      state.addresses = {};
    },
    UPDATE_USER_PROFILE(state: any, updates: Partial<UserProfile['data']>) {
      if (state.user?.data) {
        state.user.data = { ...state.user.data, ...updates };
      }
    },
    
    // Existing mutations
    UPDATE_PASSWORD_RESETING(state: any, val: { status: boolean, userData: any }) {
      state.passwordReseting.status = val.status;
      state.passwordReseting.userData = val.userData;
    },
    SET_CURRENT_PLAID_LINK(state: any, value: string) {
      state.currentPlaidLink = value;
    },
    SET_CURRENT_CHANGES_LIST(state: any, value: any[]) {
      state.changesList = value;
    },
    SET_STRIPE_SUBSCRIPTION(state: any, value: boolean) {
      state.stripeSubscriptionStatus = value;
    },
    SET_CURRENT_STRIPE_LINK(state: any, value: string) {
      state.currentStripeLink = value;
    },
    SET_WALLET(state: any, data: { network: 'BSC', address: string }) {
      state.cryptoWallets[data.network] = data.address;
    },
    SET_ADDRESS(state: any, data: { currency_id: number; address: string }) {
      state.addresses[data.currency_id] = data.address;
    },
    LOAD_ADDRESS(state: any, data: { currency_id: number; address: string }) {
      state.addresses[data.currency_id] = data.address;
    },
    SET_WALLETS(state: any, data: Balance[]) {
      state.balances = data;
    },
    SET_CURRENCIES(state: any, data: any) {
      state.currencies = data;
    },
  },
  actions: {
    async generateAddress(context: any, currency_id: number) {
      const { address } = (await api({ url: '/addresses/' + currency_id, data: {}, method: 'GET' })).data;
      context.commit('SET_ADDRESS', { currency_id, address });
      return address;
    },
    
    setIsLoggedIn(context: any) {
      context.commit('UPDATE_IS_LOGGED_IN', isLoggedIn());
    },
    startPlaidSubscription(context: any) {
      return api({ url: '/users/banks/plaid-subsription', data: {}, method: 'GET' })
        .then((response: any) => {
          if (response.data.status && response.data.data.auth_token) {
            context.commit('SET_CURRENT_PLAID_LINK', response.data.data.auth_token);
            return true;
          }
        })
    },
    loadAllChanges(context: any) {
      return api({ url: '/users/banks/changes-list', data: {}, method: 'GET' })
        .then((response: any) => {
          if (response.data.status && response.data.data) {
            context.commit('SET_CURRENT_CHANGES_LIST', response.data.data);
            return true;
          }
        })

    },
    loadStripeSubscriptionStatus(context: any) {
      return api({ url: '/users/banks/is-stripe-subscribed', data: {}, method: 'GET' })
        .then((response: any) => {
          if (response.data.status) {
            context.commit('SET_STRIPE_SUBSCRIPTION', response.data.data);
            return true;
          }
        })
    },
    startStripeSubscription(context: any) {
      return api({ url: '/users/banks/stripe-subsription', data: {}, method: 'GET' })
        .then((response: any) => {
          if (response.data.status && response.data.data.url) {
            context.commit('SET_CURRENT_STRIPE_LINK', response.data.data.url);
            return true;
          }
        })
    },
    loadBalances(context: any) {
      return api({ url: '/balances', method: 'GET' }).then((r: { data: BalancesResponse }) => {
        context.commit('SET_WALLETS', r.data.filter((v: Balance) => v.type === 'default'));
      });
    },
    authPlaidSubscription(context: any, data: { public_token: string }) {
      return api({ url: '/users/banks/plaid-subsription', data: { public_token: data.public_token }, method: 'POST' });
    },
    saveBSCWallet(context: any, data: { address: string }) {
      return api({ url: '/users/cold-wallets/set-for-net', data: { address: data.address, network: 'BSC' }, method: 'POST' });
    },
    getBSCWallet(context: any, data:any) {
      return api({ url: '/users/cold-wallets/get-for-net', params: { network: 'BSC' }, method: 'GET' }).then((r:any) => {
        context.commit('SET_WALLET', { network: r.data.data.network_type, address: r.data.data.address })
      })
    },
    startResetPassword(context: any, data: { email: string } | { email: string, code: number, new_password: string }) {
      const _data: any = data;
      if (!_data.code || !_data.new_password) {
        delete _data.code;
        delete _data.new_password;
      } else {
        _data.code = +_data.code;
      }
      return api({ url: '/users/password-reset', data: _data, method: 'POST' })
        .then((response: any) => {
          if (response.status === 201 && response.data.status && response.data.code_sent) {
            context.commit('UPDATE_PASSWORD_RESETING', { status: true, userData: data });
            return true;
          }
          if (response.status === 201 && response.data.status) {
            context.commit('UPDATE_PASSWORD_RESETING', { status: false });
            return true;
          }
          context.commit('UPDATE_PASSWORD_RESETING', { status: false });
          throw new Error(response.data.message);
        })
        .catch((e: any) => {
          if (e && e?.data?.statusCode === 400) {
            if (e?.data?.message === 'Code is not correct. You have 0 tries more' || e?.data?.message === 'Code is not active' || e?.data?.message === 'Code is expired') {
              context.commit('UPDATE_PASSWORD_RESETING', { status: false });
            }
            throw new Error(e?.data?.message || 'Unknown');
          }
          throw e;
        })
    },
    logout(context: any) {
      logOut();
      context.commit('CLEAR_USER');
      context.dispatch('setIsLoggedIn');
    },
    
    // Clear addresses for current user (useful for account switching)
    clearUserAddresses(context: any) {
      context.commit('CLEAR_SAVED_ADDRESSES');
    },
    async loadSwapPairs(context: any) {
      try {
        const response = await api({ url: '/swaps/pairs', method: 'GET' });
        return response.data;
      } catch (error) {
        console.error('Failed to load swap pairs:', error);
        throw error;
      }
    },
    async executeSwap(context: any, data: { amount_from: string; pair_id: number }) {
      try {
        const response = await api({ 
          url: '/swaps', 
          method: 'POST',
          data: data
        });
        return response.data;
      } catch (error: any) {
        console.error('Failed to execute swap:', error);
        throw error;
      }
    },
    async initWithdraw(context: any, data: { currency_id: number; address_to: string; amount_to_spend: string }) {
      try {
        const response = await api({ 
          url: '/withdraw/pass/init', 
          method: 'POST',
          data: data
        });
        return response.data;
      } catch (error: any) {
        console.error('Failed to init withdraw:', error);
        throw error;
      }
    },
    async confirmWithdraw(context: any, data: { code_token: string; code: string; id: number }) {
      try {
        const response = await api({ 
          url: '/withdraw/confirm', 
          method: 'POST',
          data: data
        });
        return response.data;
      } catch (error) {
        console.error('Failed to confirm withdraw:', error);
        throw error;
      }
    },
    async rejectWithdraw(context: any, data: { id: number }) {
      try {
        const response = await api({ 
          url: '/withdraw/reject', 
          method: 'POST',
          data: data
        });
        return response.data;
      } catch (error) {
        console.error('Failed to reject withdraw:', error);
        throw error;
      }
    },
    async loadReferrals(
      context: any,
      params?: { page?: number; level?: number; 'user.email'?: string }
    ): Promise<ReferralTreeResponse> {
      try {
        // Unified endpoint for loading referrals tree with pagination and filters
        const response = await api({ 
          url: '/users/referral/tree', 
          method: 'GET',
          params: params
        });
        
        return response.data as ReferralTreeResponse;
      } catch (error) {
        console.error('Failed to load referrals:', error);
        throw error;
      }
    },
    async loadReferralLevelStats(
      context: any,
      params?: { page?: number }
    ): Promise<ReferralLevelStatsResponse> {
      try {
        const response = await api({
          url: '/users/referral/stats',
          method: 'GET',
          params
        });

        return response.data as ReferralLevelStatsResponse;
      } catch (error) {
        console.error('Failed to load referral level stats:', error);
        throw error;
      }
    },
    async getUserProfile(context: any) {
      try {
        const response = await api({ 
          url: '/users/profile', 
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        console.error('Failed to get user profile:', error);
        throw error;
      }
    },
    async getReferralStats(context: any) {
      try {
        // Use the same loadReferrals function to get stats from first page
        // This avoids duplicate API calls to the same endpoint
        const response = await context.dispatch('loadReferrals', { page: 0 });
        
        const totalReferrals = response.totalCount || 0;
        
        return {
          total_referrals: totalReferrals,
          total_earnings: '0' // API doesn't provide earnings data yet
        };
      } catch (error) {
        console.error('Failed to get referral stats:', error);
        // Return default stats if the endpoint doesn't exist
        return {
          total_referrals: 0,
          active_referrals: 0,
          total_earnings: '0'
        };
      }
    },

    // User authentication actions
    async checkAuthStatus(context: any) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const tk = localStorage.getItem('tk');
        if (!tk) {
          context.commit('SET_AUTHENTICATED', false);
          context.commit('CLEAR_USER');
          return false;
        }
        if (tk) { 
          context.commit('SET_AUTHENTICATED', true);
          // Load user profile if authenticated
          try {
            await context.dispatch('loadUserProfile');
          } catch (error) {
            console.error('Failed to load user profile during auth check:', error);
          }
          return true;
        }
      } catch (error: any) {
        context.commit('SET_ERROR', error.message || 'Failed to check authentication status');
        return false;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async login(context: any, credentials: { email: string; password: string }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const response = await api.post('/users/login', credentials);
        
        if (response.data.status) {
          context.commit('SET_AUTHENTICATED', true);
          await context.dispatch('loadUserProfile');
          return response.data;
        } else {
          throw new Error(response.data.message || 'Login failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Login failed';
        context.commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async register(context: any, userData: any) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const response = await api.post('/users/registration', {
          ...userData,
          phone: Number(userData.phone),
          activation_code_as_link: true
        });
        
        if (response.data.status) {
          context.commit('SET_AUTHENTICATED', true);
          await context.dispatch('loadUserProfile');
          return response.data;
        } else {
          throw new Error(response.data.message || 'Registration failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
        context.commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async loadUserProfile(context: any) {
      // If profile is already loaded, return cached data
      if (context.state.profileLoaded && context.state.user) {
        return context.state.user;
      }

      try {
        context.commit('SET_PROFILE_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const response = await api.get('/users/profile');
        
        if (response.data.status) {
          context.commit('SET_USER', response.data);
          context.commit('SET_PROFILE_LOADED', true);
          return response.data;
        } else {
          throw new Error('Failed to fetch user profile');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch user profile';
        context.commit('SET_ERROR', errorMessage);
        context.commit('SET_PROFILE_LOADED', false);
        throw new Error(errorMessage);
      } finally {
        context.commit('SET_PROFILE_LOADING', false);
      }
    },
    
    // Force refresh user profile (bypass cache)
    async refreshUserProfile(context: any) {
      context.commit('SET_PROFILE_LOADED', false);
      return await context.dispatch('loadUserProfile');
    },

    async updateUserProfile(context: any, updates: any) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const response = await api.put('/users/profile', updates);
        
        if (response.data.status) {
          context.commit('UPDATE_USER_PROFILE', updates);
          return response.data;
        } else {
          throw new Error('Failed to update user profile');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update user profile';
        context.commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async verifyEmail(context: any, code: string) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');
        
        const response = await api.post('/users/verify-email', { code });
        
        if (response.data.status) {
          context.commit('UPDATE_USER_PROFILE', { is_email_verified: true });
          return response.data;
        } else {
          throw new Error('Email verification failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Email verification failed';
        context.commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    clearError(context: any) {
      context.commit('CLEAR_ERROR');
    },

    async initialize(context: any) {
      await context.dispatch('checkAuthStatus');
    },
  },
  modules: {
  }
}